'use client'

/**
 * ServiceGraphics — themed, smooth, looping animated graphics for ServiceCard.
 *
 * Animation principles:
 *   1. Single source of truth per property (never mix `whileInView` + `animate`
 *      on the same property).
 *   2. WindowFrame keeps the inner content area strictly below the 28-px
 *      header bar (`top-7`) so children using absolute positioning can never
 *      slide under the traffic-light row.
 *   3. Slow, sine-driven loops with `easeInOut` so motion feels like
 *      breathing, not ticks.
 *   4. Coordinated phase offsets so groups read as one wave.
 */

import { motion, useReducedMotion } from 'framer-motion'

/* -------------------------------------------------------------------------- */
/*  Browser window frame                                                       */
/* -------------------------------------------------------------------------- */

function WindowFrame({ children }: { children: React.ReactNode }) {
  return (
    <motion.div
      className="relative w-full aspect-[4/3] rounded-xl bg-white shadow-[0_2px_24px_rgba(15,15,15,0.05)] overflow-hidden border border-charcoal-900/[0.05]"
    >
      {/* Header bar with traffic-light dots */}
      <div className="absolute top-0 left-0 right-0 h-7 z-10 flex items-center gap-1.5 px-3 bg-brand-50/70 backdrop-blur border-b border-brand-200/40">
        <span className="w-2 h-2 rounded-full bg-brand-200" />
        <span className="w-2 h-2 rounded-full bg-brand-300" />
        <span className="w-2 h-2 rounded-full bg-brand-400" />
      </div>
      {/* Content area sits strictly below the 28-px header so children using
          `absolute inset-0` cannot bleed underneath it. */}
      <div className="absolute inset-x-0 top-7 bottom-0">{children}</div>
    </motion.div>
  )
}

/* Reusable smooth ease for ongoing loops */
const SMOOTH = [0.42, 0, 0.58, 1] as const

/* -------------------------------------------------------------------------- */
/*  Shimmer — a sweeping gradient strip used by skeleton placeholders          */
/* -------------------------------------------------------------------------- */

function Shimmer({ delay = 0, duration = 2.2 }: { delay?: number; duration?: number }) {
  const reduced = useReducedMotion()
  if (reduced) return null
  return (
    <motion.div
      aria-hidden
      className="absolute inset-0 -translate-x-full"
      style={{
        background:
          'linear-gradient(110deg, transparent 35%, rgba(255,255,255,0.85) 50%, transparent 65%)',
      }}
      animate={{ x: ['-100%', '200%'] }}
      transition={{
        duration,
        repeat: Infinity,
        repeatDelay: 0.8,
        ease: 'easeInOut',
        delay,
      }}
    />
  )
}

/* -------------------------------------------------------------------------- */
/*  1. Custom Development — SaaS dashboard with a breathing bar chart          */
/* -------------------------------------------------------------------------- */

export function CustomDevGraphic() {
  const reduced = useReducedMotion()

  const barHeights: [number, number][] = [
    [50, 64],
    [78, 60],
    [42, 56],
    [88, 72],
    [60, 78],
    [82, 70],
    [70, 84],
  ]

  return (
    <WindowFrame>
      <div className="absolute inset-0 px-3 pt-2 flex gap-3">
        {/* Sidebar */}
        <div className="w-[22%] flex flex-col gap-1.5 pt-2">
          {[0.9, 0.6, 0.7, 0.5, 0.6].map((w, i) => (
            <motion.div
              key={i}
              className="h-1.5 rounded-full bg-brand-100"
              style={{ width: `${w * 100}%` }}
              animate={reduced ? undefined : { opacity: [0.45, 0.85, 0.45] }}
              transition={{ duration: 4, repeat: Infinity, ease: SMOOTH, delay: i * 0.25 }}
            />
          ))}
        </div>

        {/* Main panel */}
        <div className="flex-1 flex flex-col gap-2 pt-2 pr-1">
          <motion.div
            className="h-2 w-1/3 rounded-full bg-brand-200"
            initial={{ scaleX: 0, originX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
          />
          <div className="grid grid-cols-3 gap-1.5">
            {[0, 1, 2].map((i) => (
              <motion.div
                key={i}
                className="h-7 rounded-md bg-brand-50/70 border border-brand-200/50 flex items-end p-1"
                initial={{ opacity: 0, y: 6 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4 + i * 0.08, duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
              >
                <motion.div
                  className="w-full h-1 rounded-full bg-brand-300"
                  initial={{ scaleX: 0, originX: 0 }}
                  whileInView={{ scaleX: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.7 + i * 0.08, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                />
              </motion.div>
            ))}
          </div>

          <div className="flex-1 flex items-end gap-1 pb-2">
            {barHeights.map(([a, b], i) => (
              <motion.div
                key={i}
                className="flex-1 rounded-t-sm bg-gradient-to-t from-brand-500 to-brand-400"
                style={{ height: `${a}%` }}
                animate={reduced ? undefined : { height: [`${a}%`, `${b}%`, `${a}%`] }}
                transition={{
                  duration: 5 + (i % 3) * 0.4,
                  repeat: Infinity,
                  ease: SMOOTH,
                  delay: i * 0.18,
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </WindowFrame>
  )
}

/* -------------------------------------------------------------------------- */
/*  2. Framer Development — landing page hero with cursor → click → cards loop */
/* -------------------------------------------------------------------------- */

export function FramerGraphic() {
  const reduced = useReducedMotion()

  /* Loop timeline (total ~7s):
   *   0.0 – 1.4s   hero + headline pills draw in
   *   1.4 – 2.6s   cursor arcs from top-right toward CTA button
   *   2.6 – 2.9s   cursor "clicks" → CTA pulses
   *   2.9 – 4.4s   3 skeleton cards appear one-by-one with shimmer
   *   4.4 – 6.0s   skeletons keep shimmering
   *   6.0 – 7.0s   everything fades to next loop
   */

  return (
    <WindowFrame>
      <div className="absolute inset-0 px-4 pt-3 flex flex-col gap-2">
        {/* Hero block */}
        <motion.div
          className="h-[36%] rounded-lg bg-brand-100/70 relative overflow-hidden"
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        >
          {/* Headline pill */}
          <motion.div
            className="absolute top-3 left-3 h-1.5 rounded-full bg-brand-500/70"
            initial={{ width: '15%' }}
            whileInView={{ width: '55%' }}
            viewport={{ once: true }}
            transition={{ delay: 0.4, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          />
          {/* Subhead pill */}
          <motion.div
            className="absolute top-7 left-3 h-1 rounded-full bg-brand-400/40"
            initial={{ width: '10%' }}
            whileInView={{ width: '40%' }}
            viewport={{ once: true }}
            transition={{ delay: 0.6, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          />

          {/* CTA button — clicks on the cursor's arrival */}
          <motion.div
            className="absolute bottom-3 left-3 h-3 w-12 rounded-md bg-brand-500"
            animate={
              reduced
                ? undefined
                : {
                    scale: [1, 1, 0.92, 1.04, 1, 1],
                    boxShadow: [
                      '0 0 0 0 rgba(125,64,255,0)',
                      '0 0 0 0 rgba(125,64,255,0)',
                      '0 0 0 0 rgba(125,64,255,0.45)',
                      '0 0 0 8px rgba(125,64,255,0.0)',
                      '0 0 0 0 rgba(125,64,255,0)',
                      '0 0 0 0 rgba(125,64,255,0)',
                    ],
                  }
            }
            transition={{
              duration: 7,
              repeat: Infinity,
              ease: SMOOTH,
              times: [0, 0.34, 0.4, 0.46, 0.55, 1],
            }}
          />

          {/* Cursor — arcs in, clicks the CTA, then exits with the loop */}
          {!reduced && (
            <motion.svg
              width="11"
              height="13"
              viewBox="0 0 11 13"
              className="absolute drop-shadow-[0_1px_2px_rgba(0,0,0,0.25)]"
              initial={{ left: '78%', top: '20%', opacity: 0 }}
              animate={{
                /* Cursor tip is the SVG's (0,0) point. The CTA button sits
                   at left-3 (≈5% of the hero width) with width w-12. To land
                   the tip squarely over the button center we aim for
                   left ≈14%, top ≈80% during the click pose. */
                left: ['78%', '78%', '18%', '14%', '14%', '78%', '78%'],
                top: ['20%', '20%', '72%', '80%', '80%', '20%', '20%'],
                opacity: [0, 1, 1, 1, 1, 0, 0],
                scale: [1, 1, 1, 0.85, 1, 1, 1],
              }}
              transition={{
                duration: 7,
                repeat: Infinity,
                ease: SMOOTH,
                times: [0, 0.18, 0.36, 0.4, 0.46, 0.6, 1],
              }}
            >
              <path d="M0 0 L10.5 4.5 L5 6 L3.5 12 Z" className="fill-brand-500" />
              <path d="M0 0 L10.5 4.5 L5 6 L3.5 12 Z" fill="none" stroke="white" strokeWidth="0.9" strokeLinejoin="round" />
            </motion.svg>
          )}
        </motion.div>

        {/* Three skeleton cards — appear after the click, with shimmer */}
        <div className="flex-1 grid grid-cols-3 gap-2 pb-3">
          {[0, 1, 2].map((i) => (
            <motion.div
              key={i}
              className="relative rounded-md bg-brand-100/70 overflow-hidden"
              initial={{ opacity: 0, y: 10 }}
              animate={
                reduced
                  ? { opacity: 0.85, y: 0 }
                  : { opacity: [0, 0, 0, 1, 1, 0.85, 0], y: [10, 10, 10, 0, 0, 0, 0] }
              }
              transition={{
                duration: 7,
                repeat: Infinity,
                ease: SMOOTH,
                times: [0, 0.4, 0.45, 0.5 + i * 0.07, 0.7, 0.86, 1],
                delay: i * 0.05,
              }}
            >
              {/* Internal placeholder rows */}
              <div className="absolute inset-2 flex flex-col gap-1.5">
                <div className="h-1 w-3/4 rounded-full bg-brand-500/55" />
                <div className="h-1 w-1/2 rounded-full bg-brand-500/35" />
                <div className="h-1 w-2/3 rounded-full bg-brand-500/35" />
              </div>
              {/* Shimmering sweep */}
              <Shimmer delay={3.6 + i * 0.25} duration={1.6} />
            </motion.div>
          ))}
        </div>
      </div>
    </WindowFrame>
  )
}

/* -------------------------------------------------------------------------- */
/*  3. SEO Optimization — SERP results with a brand row climbing to #1         */
/* -------------------------------------------------------------------------- */

export function SEOGraphic() {
  const reduced = useReducedMotion()

  /* Loop timeline (~7s):
   *   0.00 – 0.50  search bar + query fill
   *   0.50 – 1.40  4 result rows fade/slide in (stagger)
   *   1.40 – 2.80  brand row rises slot 3 → slot 0; others sink one slot each
   *   2.80 – 5.40  hold; brand row pulses with a halo + a tiny "▲ 1" badge
   *   5.40 – 7.00  fade and reset
   *
   * Result list: index 3 is the brand-highlighted "your page" that climbs.
   * Every row is absolutely positioned within a relative container so we
   * can animate `top` as a percentage of the container height (each row
   * occupies one quarter of the column).
   */
  const results = [
    { brand: false, titleW: '70%', urlW: '40%' },
    { brand: false, titleW: '60%', urlW: '36%' },
    { brand: false, titleW: '65%', urlW: '38%' },
    { brand: true, titleW: '78%', urlW: '44%' },
  ]

  return (
    <WindowFrame>
      <div className="absolute inset-0 px-4 pt-3 flex flex-col gap-2">
        {/* Search bar with typing query */}
        <motion.div
          className="h-6 rounded-full bg-brand-50/70 border border-brand-200/40 flex items-center px-3 gap-2"
          initial={{ opacity: 0, y: -4 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        >
          <svg width="10" height="10" viewBox="0 0 16 16" fill="none">
            <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.5" className="text-brand-400" />
            <path d="M11 11L14 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" className="text-brand-400" />
          </svg>
          <motion.div
            className="h-1 rounded-full bg-brand-300"
            initial={{ width: 0 }}
            whileInView={{ width: '60%' }}
            viewport={{ once: true }}
            transition={{ delay: 0.25, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          />
          {!reduced && (
            <motion.span
              aria-hidden
              className="w-px h-2 bg-brand-500"
              animate={{ opacity: [1, 1, 0, 1] }}
              transition={{ duration: 1.1, repeat: Infinity, times: [0, 0.5, 0.5, 1], ease: 'linear' }}
            />
          )}
        </motion.div>

        {/* SERP result list — absolute rows in a relative column */}
        <div className="flex-1 relative pb-2">
          {results.map((r, i) => {
            const startSlot = i
            const endSlot = r.brand ? 0 : i + 1 // brand: 3 → 0; others: i → i+1

            return (
              <motion.div
                key={i}
                className="absolute inset-x-0 h-[24%] flex items-center gap-2"
                initial={{ opacity: 0, x: -8, top: `${startSlot * 25}%` }}
                animate={
                  reduced
                    ? { opacity: 1, x: 0, top: `${endSlot * 25}%` }
                    : {
                        opacity: [0, 0, 1, 1, 1, 1, 1, 0.85, 0],
                        x: [-8, -8, 0, 0, 0, 0, 0, 0, 0],
                        top: [
                          `${startSlot * 25}%`,
                          `${startSlot * 25}%`,
                          `${startSlot * 25}%`,
                          `${startSlot * 25}%`,
                          `${endSlot * 25}%`,
                          `${endSlot * 25}%`,
                          `${endSlot * 25}%`,
                          `${endSlot * 25}%`,
                          `${endSlot * 25}%`,
                        ],
                      }
                }
                transition={{
                  duration: 7,
                  repeat: Infinity,
                  ease: SMOOTH,
                  times: [0, 0.06, 0.10 + i * 0.04, 0.28, 0.36, 0.42, 0.78, 0.85, 1],
                }}
              >
                {/* Left accent bar — brand purple for the rising row, gray otherwise */}
                <motion.span
                  className={`flex-shrink-0 w-[3px] self-stretch rounded-full ${
                    r.brand ? 'bg-brand-500' : 'bg-brand-200'
                  }`}
                  animate={
                    r.brand && !reduced
                      ? {
                          boxShadow: [
                            '0 0 0 0 rgba(125,64,255,0)',
                            '0 0 0 0 rgba(125,64,255,0)',
                            '0 0 0 0 rgba(125,64,255,0)',
                            '0 0 0 4px rgba(125,64,255,0.20)',
                            '0 0 0 0 rgba(125,64,255,0)',
                            '0 0 0 4px rgba(125,64,255,0.20)',
                            '0 0 0 0 rgba(125,64,255,0)',
                          ],
                        }
                      : undefined
                  }
                  transition={{
                    duration: 7,
                    repeat: Infinity,
                    ease: SMOOTH,
                    times: [0, 0.36, 0.42, 0.55, 0.65, 0.75, 1],
                  }}
                />
                {/* Title + URL skeleton lines */}
                <div className="flex-1 flex flex-col gap-1">
                  <div
                    className={`h-1 rounded-full ${r.brand ? 'bg-brand-500' : 'bg-brand-200'}`}
                    style={{ width: r.titleW }}
                  />
                  <div
                    className={`h-[3px] rounded-full ${r.brand ? 'bg-brand-300' : 'bg-brand-100'}`}
                    style={{ width: r.urlW }}
                  />
                </div>
                {/* Position-1 badge that fades in only on the brand row after the climb */}
                {r.brand && !reduced && (
                  <motion.span
                    aria-hidden
                    className="flex-shrink-0 flex items-center gap-0.5 px-1 py-[1px] rounded-sm bg-brand-500 text-white text-[6px] font-semibold tracking-wide leading-none"
                    initial={{ opacity: 0, scale: 0.85 }}
                    animate={{
                      opacity: [0, 0, 0, 0, 0, 1, 1, 1, 0],
                      scale: [0.85, 0.85, 0.85, 0.85, 0.85, 1, 1, 1, 0.95],
                    }}
                    transition={{
                      duration: 7,
                      repeat: Infinity,
                      ease: SMOOTH,
                      times: [0, 0.1, 0.2, 0.3, 0.42, 0.46, 0.7, 0.85, 1],
                    }}
                  >
                    <svg width="5" height="5" viewBox="0 0 10 10" fill="none" aria-hidden>
                      <path d="M5 1.5L8.5 7H1.5L5 1.5Z" fill="currentColor" />
                    </svg>
                    #1
                  </motion.span>
                )}
              </motion.div>
            )
          })}
        </div>
      </div>
    </WindowFrame>
  )
}

/* -------------------------------------------------------------------------- */
/*  4. AI Integration — chat prompt → streaming AI response with sparkles      */
/* -------------------------------------------------------------------------- */

/** Four-point sparkle / star — the universal "AI" mark. */
function Sparkle({ size = 10, className = 'fill-brand-500' }: { size?: number; className?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" className={className} aria-hidden>
      <path d="M12 2 L13.6 9.4 L21 11 L13.6 12.6 L12 20 L10.4 12.6 L3 11 L10.4 9.4 Z" />
    </svg>
  )
}

export function AIGraphic() {
  const reduced = useReducedMotion()

  /* 8s loop — full chat product UX:
   *   0.00 – 0.10  app bar + input bar already visible (static chrome)
   *   0.10 – 0.22  user message slides up
   *   0.25 – 0.35  AI bubble appears with 3 bouncing typing dots
   *   0.35 – 0.55  typing continues (dots bounce)
   *   0.55 – 0.62  dots fade out
   *   0.55 – 0.78  three response lines stream in (width grows)
   *   0.78 – 0.92  hold; caret blinks
   *   0.92 – 1.00  user message + AI bubble fade out together, restart
   */
  const EASE = [0.22, 1, 0.36, 1] as const
  const D = 8

  return (
    <WindowFrame>
      <div className="absolute inset-0 bg-white flex flex-col">
        {/* ── App bar ─────────────────────────────────── */}
        <div className="flex items-center gap-1.5 px-3 py-2 border-b border-brand-100">
          <div className="w-5 h-5 rounded-md bg-brand-500 flex items-center justify-center shadow-[0_1px_3px_rgba(125,64,255,0.3)]">
            <Sparkle size={10} className="fill-white" />
          </div>
          <div className="flex flex-col gap-0.5 flex-1">
            <span className="h-1 w-12 rounded-full bg-brand-500" />
            <div className="flex items-center gap-1">
              <span className="w-1 h-1 rounded-full bg-emerald-500" />
              <span className="h-[3px] w-6 rounded-full bg-brand-300" />
            </div>
          </div>
        </div>

        {/* ── Conversation area ──────────────────────── */}
        <div className="flex-1 px-3 py-2 flex flex-col gap-1.5 overflow-hidden relative">
          {/* User message bubble (right) */}
          <motion.div
            className="self-end max-w-[58%] rounded-[10px] rounded-br-[3px] bg-brand-500 px-2.5 py-1.5 flex flex-col gap-1 shadow-[0_2px_8px_rgba(125,64,255,0.18)]"
            initial={{ opacity: 0, y: 8 }}
            animate={reduced ? { opacity: 1, y: 0 } : {
              opacity: [0, 0, 1, 1, 0],
              y: [8, 8, 0, 0, 0],
            }}
            transition={{
              duration: D,
              repeat: Infinity,
              ease: EASE,
              times: [0, 0.10, 0.22, 0.92, 1],
            }}
          >
            <span className="h-1 w-full rounded-full bg-white/85" />
            <span className="h-1 w-[70%] rounded-full bg-white/55" />
          </motion.div>

          {/* AI response bubble (left) */}
          <motion.div
            className="self-start max-w-[78%] rounded-[10px] rounded-bl-[3px] bg-brand-50 border border-brand-200/60 px-2.5 py-2 flex gap-2 relative"
            initial={{ opacity: 0, y: 8 }}
            animate={reduced ? { opacity: 1, y: 0 } : {
              opacity: [0, 0, 0, 1, 1, 0],
              y: [8, 8, 8, 0, 0, 0],
            }}
            transition={{
              duration: D,
              repeat: Infinity,
              ease: EASE,
              times: [0, 0.20, 0.25, 0.32, 0.92, 1],
            }}
          >
            {/* AI avatar */}
            <div className="flex-shrink-0 w-4 h-4 rounded-full bg-brand-500 flex items-center justify-center mt-[1px]">
              <Sparkle size={8} className="fill-white" />
            </div>

            <div className="flex-1 min-h-[18px]">
              {/* Typing dots — visible while AI is "thinking" */}
              {!reduced && (
                <motion.div
                  className="flex gap-1 items-center h-[18px]"
                  animate={{ opacity: [0, 0, 0, 1, 1, 0, 0] }}
                  transition={{
                    duration: D,
                    repeat: Infinity,
                    ease: 'linear',
                    times: [0, 0.25, 0.32, 0.36, 0.55, 0.62, 1],
                  }}
                >
                  {[0, 1, 2].map((i) => (
                    <motion.span
                      key={i}
                      className="w-1.5 h-1.5 rounded-full bg-brand-400"
                      animate={{ y: [0, -3, 0], opacity: [0.5, 1, 0.5] }}
                      transition={{
                        duration: 0.9,
                        repeat: Infinity,
                        ease: 'easeInOut',
                        delay: i * 0.18,
                      }}
                    />
                  ))}
                </motion.div>
              )}

              {/* Streaming response lines — overlap the dots area, fade in as dots fade out */}
              <motion.div
                className="absolute inset-x-0 top-0 px-2.5 py-2 pl-[34px] flex flex-col gap-1.5"
                animate={reduced ? { opacity: 1 } : {
                  opacity: [0, 0, 0, 0, 0, 1, 1, 0],
                }}
                transition={{
                  duration: D,
                  repeat: Infinity,
                  ease: 'linear',
                  times: [0, 0.32, 0.5, 0.55, 0.58, 0.62, 0.92, 1],
                }}
              >
                {[
                  { target: '92%', start: 0.55, end: 0.64 },
                  { target: '76%', start: 0.62, end: 0.71 },
                  { target: '54%', start: 0.69, end: 0.78 },
                ].map((line, i) => (
                  <motion.div
                    key={i}
                    className="relative h-1 rounded-full bg-brand-400/60"
                    initial={{ width: '0%' }}
                    animate={reduced ? { width: line.target } : {
                      width: ['0%', '0%', '0%', line.target, line.target, '0%'],
                    }}
                    transition={{
                      duration: D,
                      repeat: Infinity,
                      ease: EASE,
                      times: [0, 0.55, line.start, line.end, 0.92, 1],
                    }}
                  >
                    {/* Caret on the last line */}
                    {i === 2 && !reduced && (
                      <motion.span
                        aria-hidden
                        className="absolute right-[-3px] top-[-1px] w-[2px] h-2 bg-brand-500 rounded-[1px]"
                        animate={{ opacity: [0, 0, 1, 0, 1, 0, 1, 0, 1, 0] }}
                        transition={{
                          duration: D,
                          repeat: Infinity,
                          ease: 'linear',
                          times: [0, 0.78, 0.80, 0.82, 0.84, 0.86, 0.88, 0.90, 0.92, 1],
                        }}
                      />
                    )}
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </motion.div>
        </div>

        {/* ── Input bar ──────────────────────────────── */}
        <div className="border-t border-brand-100 px-3 py-2 flex items-center gap-1.5">
          <div className="flex-1 h-5 rounded-full bg-brand-50 border border-brand-200/60 flex items-center px-2.5">
            <span className="h-1 w-10 rounded-full bg-brand-300/70" />
          </div>
          <motion.div
            className="w-5 h-5 rounded-full bg-brand-500 flex items-center justify-center shadow-[0_1px_4px_rgba(125,64,255,0.3)]"
            animate={reduced ? undefined : { scale: [1, 1, 0.9, 1.05, 1, 1] }}
            transition={{
              duration: D,
              repeat: Infinity,
              ease: EASE,
              times: [0, 0.16, 0.20, 0.22, 0.26, 1],
            }}
          >
            {/* paper-plane / send arrow */}
            <svg width="9" height="9" viewBox="0 0 12 12" fill="none" aria-hidden>
              <path
                d="M2 6 L10 6 M6 2 L10 6 L6 10"
                stroke="white"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </motion.div>
        </div>
      </div>
    </WindowFrame>
  )
}

/* -------------------------------------------------------------------------- */
/*  5. UX/UI Design — Figma-style artboard assembling itself on a soft loop     */
/* -------------------------------------------------------------------------- */

export function UXUIGraphic() {
  const reduced = useReducedMotion()

  /* 6s loop. Each layer fades up from a subtle Y offset, then holds, then
   * the whole artboard dims out and restarts — giving the feel of a design
   * being assembled inside Figma. A small cursor glides in at the end and
   * rests over the CTA button.
   *
   *   0.00 – 0.12   frame outline draws in
   *   0.12 – 0.24   hero image block
   *   0.24 – 0.36   heading line
   *   0.36 – 0.48   body lines
   *   0.48 – 0.62   CTA button
   *   0.62 – 0.80   cursor glides to CTA, micro "click" scale
   *   0.80 – 1.00   gentle fade-out, restart
   */

  const EASE = [0.22, 1, 0.36, 1] as const
  const DURATION = 7 // total loop seconds

  /* Each layer fades in over a long, generous window (≈1.2s) and holds
   * until the global fade-out near the end of the loop. Staggered starts
   * overlap so the page assembles like a continuous wave rather than
   * discrete pops. We animate opacity + a small scale for depth — no Y
   * offset, because Y keyframes fight the flex layout and cause jitter. */
  const layer = (start: number) => ({
    opacity: [0, 0, 1, 1, 0],
    scale: [0.96, 0.96, 1, 1, 1],
    times: [0, start, start + 0.17, 0.85, 0.96],
  })

  /* Fills the WindowFrame content area directly (no nested artboard) so
   * nothing clips. A web-page layout assembles itself: top nav → hero
   * (title + copy + CTA on the left, gradient image on the right) →
   * three feature cards. A cursor drifts in and clicks the CTA. */

  return (
    <WindowFrame>
      <div className="absolute inset-0 bg-white p-3 flex flex-col gap-2">
        {/* Top nav row */}
        <motion.div
          className="flex items-center justify-between"
          animate={reduced ? { opacity: 1 } : layer(0.06)}
          transition={{ duration: DURATION, repeat: Infinity, ease: EASE, times: layer(0.06).times }}
          style={{ transformOrigin: 'left center' }}
        >
          <div className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-brand-500" />
            <span className="h-1 w-6 rounded-full bg-brand-300" />
          </div>
          <div className="flex items-center gap-1.5">
            <span className="h-1 w-3 rounded-full bg-brand-200" />
            <span className="h-1 w-3 rounded-full bg-brand-200" />
            <span className="h-1 w-3 rounded-full bg-brand-200" />
            <span className="h-2 w-5 rounded-[3px] bg-brand-500" />
          </div>
        </motion.div>

        {/* Hero row: left copy + CTA | right gradient block */}
        <div className="flex gap-2 flex-1 min-h-0">
          {/* Left column */}
          <div className="flex-1 flex flex-col justify-center gap-1.5 pl-0.5">
            {/* Heading */}
            <motion.div
              className="flex flex-col gap-1"
              animate={reduced ? { opacity: 1 } : layer(0.22)}
              transition={{ duration: DURATION, repeat: Infinity, ease: EASE, times: layer(0.22).times }}
              style={{ transformOrigin: 'left center' }}
            >
              <div className="h-1.5 w-[85%] rounded-full bg-brand-500" />
              <div className="h-1.5 w-[60%] rounded-full bg-brand-500" />
            </motion.div>

            {/* Body copy */}
            <motion.div
              className="flex flex-col gap-0.5 mt-0.5"
              animate={reduced ? { opacity: 1 } : layer(0.30)}
              transition={{ duration: DURATION, repeat: Infinity, ease: EASE, times: layer(0.30).times }}
            >
              <div className="h-[3px] w-full rounded-full bg-brand-200" />
              <div className="h-[3px] w-[92%] rounded-full bg-brand-200" />
              <div className="h-[3px] w-[70%] rounded-full bg-brand-200" />
            </motion.div>

            {/* CTA — contains its own cursor + click ring so the click
                lands exactly on the button regardless of flex layout. */}
            <motion.div
              className="relative mt-1.5 w-14"
              animate={reduced ? { opacity: 1 } : layer(0.38)}
              transition={{ duration: DURATION, repeat: Infinity, ease: EASE, times: layer(0.38).times }}
              style={{ transformOrigin: 'left center', overflow: 'visible' }}
            >
              <div className="h-4 w-14 rounded-[4px] bg-brand-500 flex items-center justify-center">
                <span className="h-[2px] w-5 rounded-full bg-white/90" />
              </div>

              {!reduced && (
                <>
                  {/* Ripple ring outside the button border */}
                  <motion.span
                    aria-hidden
                    className="absolute -inset-[2px] rounded-[5px] border-2 border-brand-500"
                    initial={{ opacity: 0, scale: 1 }}
                    animate={{ opacity: [0, 0, 1, 0], scale: [1, 1, 1.35, 1.6] }}
                    transition={{
                      duration: DURATION,
                      repeat: Infinity,
                      ease: EASE,
                      times: [0, 0.62, 0.66, 0.76],
                    }}
                  />

                  {/* Pointer cursor — flies in from top-right and rests
                      on the button; its tip is the hotspot at (2,1.5)
                      of the 14×14 viewBox, so translate so that tip sits
                      at ~70% into the CTA width and just above center. */}
                  <motion.svg
                    aria-hidden
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    className="absolute pointer-events-none drop-shadow-[0_1px_2px_rgba(0,0,0,0.3)]"
                    style={{ transformOrigin: '2px 1.5px' }}
                    initial={{ opacity: 0, x: 60, y: -40 }}
                    animate={{
                      opacity: [0, 0, 1, 1, 1, 0],
                      x: [60, 60, 34, 34, 34, 34],
                      y: [-40, -40, 4, 4, 4, 4],
                      // tiny dip at the click moment for the "press"
                      scale: [1, 1, 1, 0.78, 1, 1],
                    }}
                    transition={{
                      duration: DURATION,
                      repeat: Infinity,
                      ease: EASE,
                      times: [0, 0.46, 0.60, 0.64, 0.70, 0.82],
                    }}
                  >
                    <path
                      d="M2 1.5 L2 11 L5 8.5 L7 12 L8.5 11.3 L6.5 7.8 L10.5 7.8 Z"
                      fill="#7D40FF"
                      stroke="white"
                      strokeWidth="0.9"
                      strokeLinejoin="round"
                    />
                  </motion.svg>
                </>
              )}
            </motion.div>
          </div>

          {/* Right gradient hero image */}
          <motion.div
            className="w-[44%] rounded-[6px] relative overflow-hidden"
            style={{
              background:
                'linear-gradient(135deg, #7D40FF 0%, #9A6AFF 55%, #D0B8FF 100%)',
            }}
            animate={reduced ? { opacity: 1 } : layer(0.14)}
            transition={{ duration: DURATION, repeat: Infinity, ease: EASE, times: layer(0.14).times }}
          >
            {/* Decorative shape motifs */}
            <span className="absolute top-2 right-2 w-3 h-3 rounded-full bg-white/30" />
            <span className="absolute bottom-2 left-2 h-1 w-6 rounded-full bg-white/50" />
            <span className="absolute bottom-4 left-2 h-1 w-4 rounded-full bg-white/30" />
          </motion.div>
        </div>

        {/* Feature cards row */}
        <motion.div
          className="flex gap-1.5 h-[22%]"
          animate={reduced ? { opacity: 1 } : layer(0.46)}
          transition={{ duration: DURATION, repeat: Infinity, ease: EASE, times: layer(0.46).times }}
        >
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="flex-1 rounded-[4px] bg-brand-50/70 border border-brand-200/50 flex flex-col justify-center gap-1 px-1.5"
            >
              <span className="w-2 h-2 rounded-[2px] bg-brand-500" />
              <span className="h-[2px] w-full rounded-full bg-brand-300" />
              <span className="h-[2px] w-[70%] rounded-full bg-brand-200" />
            </div>
          ))}
        </motion.div>

      </div>
    </WindowFrame>
  )
}

/* -------------------------------------------------------------------------- */
/*  Lookup helper                                                              */
/* -------------------------------------------------------------------------- */

export const SERVICE_GRAPHICS = {
  custom: CustomDevGraphic,
  framer: FramerGraphic,
  seo: SEOGraphic,
  ai: AIGraphic,
  uxui: UXUIGraphic,
} as const

export type ServiceGraphicKey = keyof typeof SERVICE_GRAPHICS
